import { type SxProps, type Theme, Tooltip } from '@mui/material'
import {
  DateRange,
  DateRangeCalendar,
  DateRangePickerDay,
  type DateRangePickerDayProps,
  dateRangePickerDayClasses,
} from '@mui/x-date-pickers-pro'
import { addDays, format, isAfter, isBefore, isSameDay } from 'date-fns/esm'
import { useMemo } from 'react'

import { useDaysWithOrders, useUpdateOrderCountsByDate } from '@/hooks'

import type { SelectionRange } from '../typings'
import { useOperationsCalendarData } from './hooks/useOperationsCalendarData'

type Props = {
  selection?: SelectionRange
  today: Date
  onChange: (range: SelectionRange) => void
  firstDayNotArchived: Date
  maxIntervalLength: number
  lastSelectableDate?: Date
  hasError?: boolean
}

export type SelectionAsRecord = Record<string, number>

type CustomDayProps = DateRangePickerDayProps<Date> & {
  highlightedDays?: SelectionAsRecord
  firstDayNotArchived: Date
}

export function OperationsCalendar(props: Props) {
  const {
    firstDayNotArchived,
    onChange,
    selection,
    maxIntervalLength,
    lastSelectableDate,
    hasError,
  } = props

  // Updates order counts at every open
  useUpdateOrderCountsByDate()

  const daysWithOrders = useDaysWithOrders()
  const { calendarData } = useOperationsCalendarData(firstDayNotArchived)

  const value = useMemo<DateRange<Date>>(
    () => [selection?.min || null, selection?.max || null],
    [selection],
  )

  // Calculate the last selectable date based on firstDayNotArchived and maxIntervalLength
  const calculatedLastSelectableDate = addDays(firstDayNotArchived, maxIntervalLength - 1)

  // Use the earlier of calculatedLastSelectableDate and lastSelectableDate (if provided)
  const effectiveLastSelectableDate =
    lastSelectableDate && isBefore(lastSelectableDate, calculatedLastSelectableDate)
      ? lastSelectableDate
      : calculatedLastSelectableDate

  return (
    <DateRangeCalendar
      sx={{
        ...(hasError && {
          '& .MuiDateRangePickerDay-rangeIntervalDayHighlight': {
            backgroundColor: 'error.main',
          },
          '& .MuiDateRangePickerDay-rangeIntervalDayHighlightStart': {
            backgroundColor: 'error.main',
          },
          '& .MuiDateRangePickerDay-rangeIntervalDayHighlightEnd': {
            backgroundColor: 'error.main',
          },
          '& .MuiPickersDay-root.Mui-selected': {
            backgroundColor: 'error.main',
            color: 'white',
          },
        }),
      }}
      calendars={1}
      value={value}
      onChange={(newValue: DateRange<Date>) => {
        const value = {
          min: newValue[0] === null ? undefined : newValue[0],
          max: newValue[1] !== null ? newValue[1] : newValue[0] === null ? undefined : newValue[0],
        }

        onChange(value)
      }}
      minDate={firstDayNotArchived}
      maxDate={effectiveLastSelectableDate}
      shouldDisableDate={date => {
        if (isBefore(date, firstDayNotArchived) || isAfter(date, effectiveLastSelectableDate)) {
          return true
        }

        const formattedDate = format(date, 'yyyyMMdd')
        if (formattedDate in calendarData.disabledDays) {
          return true
        }
        return false
      }}
      slots={{
        day: CustomDay,
      }}
      slotProps={{
        day: {
          firstDayNotArchived,
          highlightedDays: daysWithOrders,
        } as any, // TypeScript error workaround as suggested on MUI-X docs
      }}
    />
  )
}

const CustomDay = (props: CustomDayProps) => {
  const { day, firstDayNotArchived, highlightedDays, disabled, ...other } = props

  const dayMonth = format(day, 'MM')
  const firstDayMonth = format(firstDayNotArchived, 'MM')
  const isFirstDayNotArchived = isSameDay(day, firstDayNotArchived) && dayMonth === firstDayMonth

  const formattedDay = format(day, 'yyyyMMdd')
  const orderCount = highlightedDays?.[formattedDay] || 0
  const showDot = orderCount > 0 && !disabled
  const tooltipText = showDot ? `${orderCount} orders` : ''

  const sx: SxProps<Theme> = {
    position: 'relative',
    // Archived days separator
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '10%',
      height: '80%',
      width: '2px',
      display: isFirstDayNotArchived ? 'block' : 'none',
      backgroundColor: theme => theme.vars.palette.primary.main,
    },

    // Green dot
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: '50%',
      width: '3px',
      height: '3px',
      zIndex: 5,
      display: showDot ? 'block' : 'none',
      borderRadius: '50%',
      transform: 'translate(-50%, -7px)',
      background: theme => theme.vars.palette.success.light,

      [`.${dateRangePickerDayClasses.hiddenDayFiller} + &`]: {
        display: 'none',
      },
    },
    [`&.${dateRangePickerDayClasses.hiddenDayFiller}::before`]: {
      display: 'none',
    },
  }

  const dayComponent = <DateRangePickerDay {...other} disabled={disabled} sx={sx} day={day} />

  return showDot ? (
    <Tooltip title={tooltipText} arrow>
      {dayComponent}
    </Tooltip>
  ) : (
    dayComponent
  )
}
